import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronUp, ExternalLink, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import PlannerLinkManager from '@/components/common/planner-link-manager'
import { OrderPlanningAllocationPanel } from '@/components/orders/order-planning-allocation-panel'
import PlanningRequirementJsonDialog from '@/components/orders/planning-requirement-json-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { formatJobTitleWithId } from '@/components/jobs/job-title-link'
import { cn } from '@/lib/utils'
import { getErrorMessage } from '@/lib/api'
import { getPlannerLinkResourceLabel } from '@/lib/planner-links'
import {
  formatServiceInstanceLabel,
  resolveJobNavigationServiceInstanceId,
  type ServiceInstanceBrief,
} from '@/lib/planner-job-instance'
import { withServiceInstanceId } from '@/lib/service-instance'
import resourcePlannerApi from '@/services/resource-planner'
import type { StoreOrderDetail } from '@/types'

type OrderResourcePlanningPanelProps = {
  order: StoreOrderDetail
  referenceId?: string | null
  serviceInstanceId?: string | number | null
  showLinkManager?: boolean
  onChanged?: () => void | Promise<void>
}

const getJobDetailsPath = (jobId: string | number, serviceInstanceId?: string | number | null) =>
  withServiceInstanceId(
    `/job/${jobId}`,
    serviceInstanceId != null ? String(serviceInstanceId) : undefined,
  )

const getJobsListPathForOrder = (referenceId: string | number, serviceInstanceId?: string | number | null) => {
  const params = new URLSearchParams()
  params.set('order_id', String(referenceId))
  return withServiceInstanceId(
    `/job?${params.toString()}`,
    serviceInstanceId != null ? String(serviceInstanceId) : undefined,
  )
}

const formatJobScopeLabel = (scope?: string | null) => {
  const normalized = String(scope ?? 'single_job').toLowerCase()
  if (normalized === 'mixed') return 'Order job + line item jobs'
  if (normalized === 'per_order_item' || normalized === 'per_item') return 'One job per line item'
  if (normalized === 'per_resource') return 'One job per resource'
  return 'One job per booking'
}

const toPkValue = (value: string | number | null | undefined): number | null => {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value >= 0 ? value : null
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed || !/^\d+$/.test(trimmed)) {
      return null
    }

    const parsed = Number(trimmed)
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : null
  }

  return null
}

export function OrderResourcePlanningPanel({
  order,
  referenceId,
  serviceInstanceId,
  showLinkManager = true,
  onChanged,
}: OrderResourcePlanningPanelProps) {
  const queryClient = useQueryClient()
  const [resourcePlanningOpen, setResourcePlanningOpen] = useState(false)
  const effectiveReferenceId = referenceId ?? order.reference_id
  const normalizedOrderId = useMemo(() => toPkValue(order.raw_id), [order.raw_id])

  const plannerContractQuery = useQuery({
    queryKey: ['planner-order-contract-preview', normalizedOrderId ?? null, order.reference_id],
    queryFn: () => resourcePlannerApi.buildContractForOrder(
      normalizedOrderId != null
        ? { order_id: normalizedOrderId }
        : { reference_id: order.reference_id },
    ),
    enabled: normalizedOrderId != null || Boolean(order.reference_id),
    staleTime: 30_000,
  })

  const matchedPlannerLinkCount = useMemo(() => {
    const context = plannerContractQuery.data?.context
    if (context && typeof context.matched_link_count === 'number') {
      return context.matched_link_count
    }
    return null
  }, [plannerContractQuery.data?.context])

  const resolvedJobScope = plannerContractQuery.data?.job_scope ?? order.job_scope ?? null
  const matchedPlannerModels = useMemo(() => {
    const models = plannerContractQuery.data?.context?.planner_models
    return Array.isArray(models) ? models : []
  }, [plannerContractQuery.data?.context?.planner_models])

  const plannerReadyForJobs = matchedPlannerLinkCount != null
    ? matchedPlannerLinkCount > 0
    : ((order.planner_resource_links?.length ?? 0) > 0)

  const contractWorkItemCount = useMemo(() => {
    const workItems = plannerContractQuery.data?.work?.work_items
    return Array.isArray(workItems) ? workItems.length : null
  }, [plannerContractQuery.data?.work?.work_items])

  const usesOrderLevelPlannerOnly = useMemo(() => {
    type PlannerResourceCapability = {
      metadata?: {
        sources?: Array<{ source?: string }>
      }
    }
    const requirements = (plannerContractQuery.data?.resource_capabilities ?? []) as PlannerResourceCapability[]
    return requirements.every((requirement) => {
      const sources = requirement.metadata?.sources
      return Array.isArray(sources) && sources.length > 0
        && sources.every((source) => String(source.source ?? '') === 'order')
    })
  }, [plannerContractQuery.data?.resource_capabilities])

  const plannerScopeMismatch = useMemo(() => {
    const scope = String(resolvedJobScope ?? '').toLowerCase()
    return (scope === 'per_order_item' || scope === 'per_item')
      && (contractWorkItemCount === 0 || usesOrderLevelPlannerOnly)
  }, [contractWorkItemCount, resolvedJobScope, usesOrderLevelPlannerOnly])

  const contractContext = plannerContractQuery.data?.context

  const bookingServiceInstanceLabel = useMemo(() => {
    const fromContract = contractContext?.booking_service_instance as ServiceInstanceBrief | undefined
    return formatServiceInstanceLabel(fromContract)
      ?? formatServiceInstanceLabel({
        instance_id: order.service_instance_id ?? undefined,
        name: order.service_instance_name ?? undefined,
      })
      ?? formatServiceInstanceLabel({ instance_id: String(serviceInstanceId ?? '') || undefined })
  }, [contractContext?.booking_service_instance, order.service_instance_id, order.service_instance_name, serviceInstanceId])

  const jobManagementInstanceLabel = useMemo(() => {
    const fromContract = contractContext?.job_service_instance as ServiceInstanceBrief | undefined
    return formatServiceInstanceLabel(fromContract)
  }, [contractContext?.job_service_instance])

  const jobNavigationServiceInstanceId = useMemo(() => resolveJobNavigationServiceInstanceId({
    contractContext,
    jobServiceInstanceId: order.job_service_instance_id,
    jobsSummary: order.jobs_summary,
    bookingServiceInstanceId: order.service_instance_id ?? serviceInstanceId,
  }), [contractContext, order.job_service_instance_id, order.jobs_summary, order.service_instance_id, serviceInstanceId])

  const usesSeparateJobManagementInstance = useMemo(() => {
    const bookingId = String(contractContext?.booking_service_instance?.instance_id ?? order.service_instance_id ?? '').trim()
    const jobId = String(contractContext?.job_service_instance?.instance_id ?? jobNavigationServiceInstanceId ?? '').trim()
    return Boolean(bookingId && jobId && bookingId !== jobId)
  }, [contractContext, jobNavigationServiceInstanceId, order.service_instance_id])

  const manualPlannerLinks = useMemo(
    () => (order.planner_resource_links ?? []).filter((link) => String(link.job_creation_mode ?? '').toLowerCase() === 'manual'),
    [order.planner_resource_links],
  )

  const triggerPlannerJobsMutation = useMutation({
    mutationFn: () => {
      if (order.raw_id == null) {
        throw new Error('This booking does not expose a usable internal id for planner job creation')
      }

      return resourcePlannerApi.triggerJobsForOrder({
        order_id: order.raw_id,
        trigger_mode: 'manual',
      })
    },
    onSuccess: async (result) => {
      const assignedSuffix = typeof result.assigned_worker_count === 'number' && result.assigned_worker_count > 0
        ? ` (${result.assigned_worker_count} worker${result.assigned_worker_count === 1 ? '' : 's'} auto-assigned)`
        : ''
      const jobLabel = result.job_ref ? `#${result.job_ref}` : (result.job_id != null ? `ID ${result.job_id}` : null)
      const jobCount = Array.isArray(result.job_ids) ? result.job_ids.length : (result.job_id != null ? 1 : 0)
      const jobCountSuffix = jobCount > 1 ? ` • ${jobCount} jobs` : jobLabel ? ` • Job ${jobLabel}` : ''
      const scopeSuffix = result.job_scope ? ` (${formatJobScopeLabel(result.job_scope)})` : ''

      if (result.action === 'created') {
        toast.success(`Job${jobCount > 1 ? 's' : ''} created from planner defaults${assignedSuffix}${jobCountSuffix}${scopeSuffix}`)
      } else if (result.action === 'existing') {
        toast.success(`Existing job${jobCount > 1 ? 's' : ''} refreshed${assignedSuffix}${jobCountSuffix}${scopeSuffix}`)
      } else if (result.action === 'noop') {
        toast(
          result.matched_link_count === 0
            ? 'No planner links match this booking. Configure Planner → Planner Models → Execution Steps or package planner resources, then reopen this booking.'
            : result.detail === 'links_matched_but_no_jobs_created'
              ? `Planner links matched (${result.matched_link_count}), but no jobs could be created. Check the booking has line items and the Home Services job scope matches your setup.`
              : 'Planner links matched, but no jobs were created for this booking.',
        )
      } else if (result.action === 'skipped') {
        toast.error('Planner links matched, but the job could not be created')
      } else {
        toast.success(`Planner job sync completed${assignedSuffix}${jobCountSuffix}${scopeSuffix}`)
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['order-detail', effectiveReferenceId] }),
        queryClient.invalidateQueries({ queryKey: ['planner-order-contract-preview', normalizedOrderId ?? null, order.reference_id] }),
        queryClient.invalidateQueries({ queryKey: ['commerce-orders'] }),
        queryClient.invalidateQueries({ queryKey: ['jobs'] }),
        onChanged?.(),
      ])
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const linkedJobId = order.job_id ?? null
  const linkedJobCount = typeof order.linked_job_count === 'number'
    ? order.linked_job_count
    : (order.jobs_summary?.length ?? (order.has_job ? 1 : 0))
  const jobsSummary = order.jobs_summary ?? []

  return (
    <div className="space-y-4">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-2"
        aria-expanded={resourcePlanningOpen}
        onClick={() => setResourcePlanningOpen((open) => !open)}
      >
        {resourcePlanningOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        {resourcePlanningOpen ? 'Hide resource planning' : 'Show resource planning'}
      </Button>

      {resourcePlanningOpen ? (
        <div className="space-y-3 rounded-xl border border-border/70 bg-muted/20 p-4">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Resource planning</p>
          <p className="text-sm text-muted-foreground">
            Planner links on this booking drive execution jobs. Automatic triggers run on status change; manual triggers are available when configured.
          </p>
          {resolvedJobScope ? (
            <Badge variant="outline" className="mt-2 border-sky-200 bg-sky-50 text-sky-800">
              {formatJobScopeLabel(resolvedJobScope)}
            </Badge>
          ) : null}
        </div>

        <div className={cn(
          'rounded-lg border p-3 text-sm',
          plannerReadyForJobs
            ? 'border-emerald-200 bg-emerald-50/80 text-emerald-950'
            : 'border-amber-200 bg-amber-50/80 text-amber-950',
        )}>
          <p className="font-medium">
            {plannerContractQuery.isLoading
              ? 'Checking planner linkage…'
              : plannerReadyForJobs
                ? 'Planner is linked to this booking'
                : 'Planner is not linked to this booking yet'}
          </p>
          {plannerContractQuery.isLoading ? (
            <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Building planning contract preview…
            </div>
          ) : plannerReadyForJobs ? (
            <div className="mt-2 space-y-1 text-xs">
              <p>
                Matched planner links: <span className="font-medium">{matchedPlannerLinkCount ?? '—'}</span>
              </p>
              {matchedPlannerModels.length > 0 ? (
                <p>
                  Planner models:{' '}
                  <span className="font-medium">
                    {matchedPlannerModels.map((model) => model.name || model.code).filter(Boolean).join(', ')}
                  </span>
                </p>
              ) : null}
              <div className="mt-2 space-y-1 rounded-md border border-sky-200 bg-sky-50/70 px-2.5 py-2 text-sky-950">
                <p className="font-medium">Execution instances</p>
                <p>
                  Booking branch:{' '}
                  <span className="font-medium">{bookingServiceInstanceLabel ?? '—'}</span>
                </p>
                <p>
                  Job management:{' '}
                  <span className="font-medium">
                    {jobManagementInstanceLabel ?? 'Not configured on planner model'}
                  </span>
                </p>
                {usesSeparateJobManagementInstance ? (
                  <p className="text-sky-900/80">
                    Jobs are created on the job management branch. Job links below open under that instance, not the booking branch.
                  </p>
                ) : jobManagementInstanceLabel ? (
                  <p className="text-sky-900/80">
                    Bookings and jobs currently use the same branch instance.
                  </p>
                ) : (
                  <p className="text-sky-900/80">
                    Set <span className="font-medium">Job management instance</span> on the planner model in Planner Studio so jobs are created on the correct branch.
                  </p>
                )}
              </div>
              <p className="text-emerald-900/80">
                Trigger jobs should work from here or the bookings action menu.
              </p>
              {plannerScopeMismatch ? (
                <p className="text-amber-900">
                  This booking uses global order defaults only. Jobs will be created as one booking-level job even though Home Services is set to per line item.
                </p>
              ) : null}
            </div>
          ) : (
            <ol className="mt-2 list-decimal space-y-1 pl-4 text-xs">
              <li>
                Open <span className="font-medium">Planner → Planner Models → Execution Steps</span> for the same branch as this booking.
              </li>
              <li>
                Add an execution setup for this booking&apos;s order type with a planner model and resource.
              </li>
              <li>
                Or edit each service package on the booking and set a <span className="font-medium">Planner model</span> plus package resources.
              </li>
              <li>
                Reopen this booking and confirm <span className="font-medium">Requirement JSON</span> shows resources (not an empty list).
              </li>
            </ol>
          )}
        </div>

        {order.planner_resource_links && order.planner_resource_links.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {order.planner_resource_links.map((link) => (
              <Badge key={String(link.id)} variant="outline" className="border-amber-200 bg-amber-50 text-amber-800">
                {getPlannerLinkResourceLabel(link)} x{link.quantity ?? 1}{link.serve_type_label ? ` • ${link.serve_type_label}` : ''}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No booking-wide planner defaults matched this order yet.</p>
        )}

        {manualPlannerLinks.length > 0 ? (
          <p className="text-xs text-muted-foreground">
            Manual job creation is enabled for at least one matched planner default on this booking.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            Auto job creation runs when order status matches planner trigger rules configured on catalog or global links.
          </p>
        )}

        {jobsSummary.length > 0 ? (
          <div className="space-y-2 rounded-lg border border-border/60 bg-background/70 p-3">
            <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">Linked jobs</p>
            <div className="space-y-2">
              {jobsSummary.map((job) => (
                <div key={String(job.id)} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <div>
                    <Link
                      to={getJobDetailsPath(job.id, job.service_instance_id ?? jobNavigationServiceInstanceId)}
                      className="font-medium text-primary hover:underline"
                    >
                      {formatJobTitleWithId({
                        jobId: job.id,
                        title: job.title,
                        jobRef: job.job_ref,
                      })}
                    </Link>
                    <p className="text-xs text-muted-foreground">
                      {job.order_item_id ? 'Line item job' : 'Booking job'}
                      {job.status ? ` • ${job.status}` : ''}
                      {typeof job.assigned_worker_count === 'number' && job.assigned_worker_count > 0
                        ? ` • ${job.assigned_worker_count} assigned`
                        : ''}
                    </p>
                  </div>
                  <Button asChild variant="outline" size="sm" className="h-7 px-2 text-xs">
                    <Link to={getJobDetailsPath(job.id, job.service_instance_id ?? jobNavigationServiceInstanceId)}>
                      Open
                      <ExternalLink className="ml-1 h-3 w-3" />
                    </Link>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {order.has_job || linkedJobCount > 0 ? (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>
              {linkedJobCount > 1 ? `${linkedJobCount} linked jobs` : 'A linked execution job exists for this booking.'}
            </span>
            {linkedJobCount > 1 ? (
              <Button asChild variant="outline" size="sm" className="h-7 px-2 text-xs">
                <Link to={getJobsListPathForOrder(order.reference_id, jobNavigationServiceInstanceId)}>
                  View jobs
                  <ExternalLink className="ml-1 h-3 w-3" />
                </Link>
              </Button>
            ) : linkedJobId != null ? (
              <Button asChild variant="outline" size="sm" className="h-7 px-2 text-xs">
                <Link to={getJobDetailsPath(linkedJobId, jobNavigationServiceInstanceId)}>
                  View job
                  <ExternalLink className="ml-1 h-3 w-3" />
                </Link>
              </Button>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <PlanningRequirementJsonDialog order={order} />
          {manualPlannerLinks.length > 0 ? (
            <Button
              type="button"
              variant="default"
              onClick={() => triggerPlannerJobsMutation.mutate()}
              disabled={triggerPlannerJobsMutation.isPending || order.raw_id == null}
            >
              {triggerPlannerJobsMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Trigger jobs
            </Button>
          ) : (
            <Button
              type="button"
              variant="secondary"
              onClick={() => triggerPlannerJobsMutation.mutate()}
              disabled={triggerPlannerJobsMutation.isPending || order.raw_id == null}
            >
              {triggerPlannerJobsMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Sync jobs now
            </Button>
          )}
        </div>
        </div>
      ) : null}

      <div className="space-y-3 rounded-xl border border-border/70 bg-muted/20 p-4">
        <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Allocation requirements</p>
        <OrderPlanningAllocationPanel order={order} />
      </div>

      {showLinkManager ? (
        <PlannerLinkManager
          title="Order-level planner resources"
          description="Manage resources that apply to this booking as a whole."
          targetType="order"
          targetId={order.raw_id ?? null}
          serviceInstanceId={order.service_instance ?? serviceInstanceId ?? null}
          initialLinks={order.planner_resource_links ?? []}
          onChanged={async () => {
            await onChanged?.()
          }}
        />
      ) : null}
    </div>
  )
}

export default OrderResourcePlanningPanel
